import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { ThemePreference } from "../store/ui-store";
import { settingsRepository } from "../../db";

const THEME_KEY = "dua-workspace:theme";

interface ThemeContextValue {
  preference: ThemePreference;
  resolvedTheme: "light" | "dark";
  setPreference: (theme: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredTheme(): ThemePreference {
  const stored = localStorage.getItem(THEME_KEY);
  return stored === "light" || stored === "dark" || stored === "system" ? stored : "system";
}

function getSystemTheme(): "light" | "dark" {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredTheme);
  const [systemTheme, setSystemTheme] = useState<"light" | "dark">(getSystemTheme);
  const resolvedTheme = preference === "system" ? systemTheme : preference;

  const setPreference = useCallback((theme: ThemePreference) => {
    localStorage.setItem(THEME_KEY, theme);
    setPreferenceState(theme);
  }, []);

  useEffect(() => {
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => setSystemTheme(query.matches ? "dark" : "light");
    query.addEventListener("change", handleChange);
    return () => query.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    void settingsRepository.get().then((settings) => {
      const local = localStorage.getItem(THEME_KEY);
      if (local === null) {
        localStorage.setItem(THEME_KEY, settings.appearance.theme);
        setPreferenceState(settings.appearance.theme);
      }
      document.documentElement.dataset.reducedMotion = settings.appearance.reducedMotion === "reduce"
        ? "reduce"
        : settings.appearance.reducedMotion === "no_preference"
          ? "no-preference"
          : window.matchMedia("(prefers-reduced-motion: reduce)").matches
            ? "reduce"
            : "no-preference";
    }).catch(() => undefined);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = resolvedTheme;
    document.documentElement.style.colorScheme = resolvedTheme;
    document.querySelector('meta[name="theme-color"]')?.setAttribute(
      "content",
      resolvedTheme === "dark" ? "#171c19" : "#f4f0e7"
    );
  }, [resolvedTheme]);

  const value = useMemo<ThemeContextValue>(() => ({
    preference,
    resolvedTheme,
    setPreference
  }), [preference, resolvedTheme, setPreference]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// The paired hook intentionally shares this small provider module.
// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error("useTheme must be used inside ThemeProvider");
  return value;
}
